import axios, { type AxiosRequestConfig } from 'axios';
import { usePortalAuthStore } from '@/stores/portal-auth.js';

export interface ApiEnvelope<T, M = unknown> {
  data: T;
  meta?: M;
}

const apiBaseUrl =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? '' : 'http://localhost:3001');

export const portalApiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
});

portalApiClient.interceptors.request.use((config) => {
  const token = usePortalAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      usePortalAuthStore.getState().clearAuth();
      const path = window.location.pathname;
      if (path.startsWith('/portal') && !path.startsWith('/portal/login') && !path.startsWith('/portal/t/')) {
        window.location.assign('/portal/login');
      }
    }
    return Promise.reject(error);
  },
);

async function portalGet<T, M = unknown>(url: string, config?: AxiosRequestConfig) {
  const res = await portalApiClient.get<ApiEnvelope<T, M>>(url, config);
  return res.data;
}

async function portalPost<T, B = unknown>(url: string, body?: B) {
  const res = await portalApiClient.post<ApiEnvelope<T>>(url, body);
  return res.data;
}

export const portalAuthApi = {
  login: (body: { email: string; password: string; tenantSlug?: string }) =>
    portalPost<{
      token: string;
      customer: {
        id: string;
        firstName: string;
        lastName: string | null;
        email: string | null;
        portalCanCreateTickets: boolean;
      };
    }>('/api/v1/portal/auth/login', body),
  forgotPassword: (body: { email: string; tenantSlug?: string }) =>
    portalPost<{ message: string }>('/api/v1/portal/auth/forgot-password', body),
  resetPassword: (body: { token: string; newPassword: string }) =>
    portalPost<{ success: boolean }>('/api/v1/portal/auth/reset-password', body),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    portalPost<{ success: boolean }>('/api/v1/portal/auth/change-password', body),
};

export type PortalMe = {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  loyaltyPointsBalance: number;
  storeCreditBalance: string;
  canCreateTickets: boolean;
  totalTickets: number;
  totalSpend: string;
  storeId?: string;
};

export type PortalTicketCard = {
  id: string;
  ticketNumber: string;
  status: string;
  deviceSummary: string;
  repairSummary: string;
  createdAt: string;
  promisedDate: string | null;
  total: string;
  balanceDue: string;
  warrantyType?: 'days' | 'lifetime' | 'none' | null;
  warrantyDays?: number | null;
  warrantyExpiresAt: string | null;
  progressIndex: number;
};

export const portalApi = {
  me: () => portalGet<PortalMe>('/api/v1/portal/me').then((r) => r.data),
  tickets: (params?: { status?: string; page?: number; pageSize?: number }) =>
    portalGet<PortalTicketCard[], { page: number; pageSize: number; total: number }>(
      '/api/v1/portal/tickets',
      { params },
    ),
  ticket: (id: string) => portalGet<Record<string, unknown>>(`/api/v1/portal/tickets/${id}`).then((r) => r.data),
  addNote: (id: string, message: string) =>
    portalPost(`/api/v1/portal/tickets/${id}/notes`, { message }),
  createTicket: (body: {
    deviceMake: string;
    deviceModel: string;
    issueDescription: string;
    preferredDate?: string;
    additionalNotes?: string;
  }) => portalPost('/api/v1/portal/tickets', body),
  invoices: () => portalGet<Array<Record<string, unknown>>>('/api/v1/portal/invoices').then((r) => r.data),
  estimates: () => portalGet<Array<Record<string, unknown>>>('/api/v1/portal/estimates').then((r) => r.data),
  loyalty: () => portalGet<Record<string, unknown>>('/api/v1/portal/loyalty').then((r) => r.data),
  appointments: () => portalGet<Array<Record<string, unknown>>>('/api/v1/portal/appointments').then((r) => r.data),
  cancelAppointment: (id: string) =>
    portalPost<{ cancelled: boolean }>(`/api/v1/portal/appointments/${id}/cancel`).then((r) => r.data),
};

export const publicPortalApi = {
  ticketByToken: (token: string) =>
    axios
      .get<ApiEnvelope<Record<string, unknown>>>(`${apiBaseUrl}/api/v1/public/portal/ticket/${token}`)
      .then((r) => r.data.data),
  track: (params: { phone: string; ticketNumber?: string; tenantSlug?: string }) =>
    axios
      .get<ApiEnvelope<{ found: boolean; tickets: Array<Record<string, unknown>> }>>(
        `${apiBaseUrl}/api/v1/public/portal/track`,
        { params },
      )
      .then((r) => r.data.data),
  getEstimate: (token: string) =>
    axios
      .get<ApiEnvelope<Record<string, unknown>>>(`${apiBaseUrl}/api/v1/public/estimates/${token}`)
      .then((r) => r.data.data),
  approveEstimate: (token: string, body: { approvedByName: string; signatureDataUrl?: string }) =>
    axios
      .post<ApiEnvelope<{ success: boolean; ticketNumber: string | null }>>(
        `${apiBaseUrl}/api/v1/public/estimates/${token}/approve`,
        body,
      )
      .then((r) => r.data.data),
  declineEstimate: (token: string, body?: { reason?: string }) =>
    axios
      .post<ApiEnvelope<{ success: boolean }>>(
        `${apiBaseUrl}/api/v1/public/estimates/${token}/decline`,
        body ?? {},
      )
      .then((r) => r.data.data),
};
